import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useAppointments } from './useAppointments';
import { useBlockTime, useDeleteBlockedTime } from '../queries/useBlockTimeMutation';
import { convertGroupedDataToEvents, getAppointmentsForDay, getMonthRange } from '@/lib/utils/calendarUtils';
import type { StanFilterValue } from '@/components/core-components/StanFilters/types';
import type { Appointment } from '../types/appointment.types';
import { APPOINTMENT_TYPES } from '../constants/appointmentData';

export function useAppointmentsPage() {
    const location = useLocation();
    const { push } = useStanRouter();
    const [searchParams, setSearchParams] = useSearchParams();

    const getInitialFilters = (): StanFilterValue[] => {
        const filtersParam = searchParams.get('filters');
        if (filtersParam) {
            try {
                return JSON.parse(filtersParam);
            } catch {
                return [];
            }
        }
        return [];
    };

    const getInitialPage = (): number => {
        const pageParam = searchParams.get('page');
        return pageParam ? parseInt(pageParam, 10) : 1;
    };

    const getInitialCalendarDate = (): Date => {
        const monthParam = searchParams.get('month');
        if (monthParam) {
            try {
                const date = new Date(monthParam);
                if (!isNaN(date.getTime())) {
                    return date;
                }
            } catch {
                // Fall through to default
            }
        }
        return new Date();
    };

    const [filterValues, setFilterValues] = useState<StanFilterValue[]>(getInitialFilters);
    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [pageSize] = useState(10);
    const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date>(getInitialCalendarDate);

    // Determine view mode from URL - default to calendar
    const getViewModeFromUrl = () => {
        const pathSegments = location.pathname.split('/');
        const viewParam = pathSegments[2];
        return viewParam === 'list' ? 'list' : 'calendar';
    };

    const [viewMode, setViewMode] = useState<'calendar' | 'list'>(getViewModeFromUrl);

    // Get current month range for calendar view (based on selected date)
    const { startOfMonth, endOfMonth } = useMemo(() => getMonthRange(selectedCalendarDate), [selectedCalendarDate]);

    // For list view, determine API parameters based on filters
    const getListApiParams = useMemo(() => {
        const baseParams = {
            page: currentPage,
            pageSize,
        };

        if (filterValues.length === 0) {
            return baseParams;
        }

        const dateFilters = filterValues.filter(f => f.id === 'date');
        const statusFilter = filterValues.find(f => f.id === 'status');

        const params: Record<string, unknown> = { ...baseParams };

        // Add date range if date filter is applied
        if (dateFilters.length > 0) {
            const fromDateFilter = dateFilters.find(f => f.operator === 'gte');
            const toDateFilter = dateFilters.find(f => f.operator === 'lte');

            if (fromDateFilter) {
                params.startDate = fromDateFilter.value;
            }
            if (toDateFilter) {
                params.endDate = toDateFilter.value;
            }
        }

        // Add status filter if status filter is applied
        if (statusFilter) {
            const apiStatus = statusFilter.value === 'canceled' ? 0 : 2;
            params.bookingStatus = apiStatus;
        }

        return params;
    }, [filterValues, currentPage, pageSize]);

    // Fetch data for calendar view (selected month)
    const {
        appointments: calendarAppointments,
        groupedByDate,
        isLoading: calendarLoading,
        isError: calendarIsError,
        error: calendarError,
        refetch: refetchCalendar,
    } = useAppointments({
        startDate: startOfMonth,
        endDate: endOfMonth,
        bookingStatus: 2, // Only fetch active bookings (not cancelled) like Vue does
        pageSize: 9999, // Fetch all bookings for the month (no pagination for calendar view)
    });

    const calendarEvents = useMemo(() => convertGroupedDataToEvents(groupedByDate), [groupedByDate]);

    const { blockTime, isBlocking } = useBlockTime();
    const { deleteBlockedTime, isDeleting } = useDeleteBlockedTime();

    // Fetch data for list view (based on filters)
    const listApiParams = getListApiParams;

    const {
        appointments: listAppointments,
        isLoading: listLoading,
        isError: listIsError,
        error: listError,
        pagination,
        refetch: refetchList,
    } = useAppointments(listApiParams);

    // For list view, we should only show appointments (not blocked times or Google Calendar events)
    const listAppointmentsFiltered = listAppointments.filter(apt => {
        // Only show actual bookings, filter out blocked times and Google Calendar events
        if (apt.type === APPOINTMENT_TYPES.BLOCKED || apt.type === APPOINTMENT_TYPES.GOOGLE_MEET) return false;

        // Apply title search filter if it exists
        if (filterValues.length === 0) return true;

        return filterValues.every(filter => {
            // Only title filtering is done client-side, status and date are handled by API
            if (filter.id === 'title') {
                const titleValue = apt.title.toLowerCase();
                const filterValue = (filter.value as string).toLowerCase();

                if (filter.operator === 'eq') {
                    return titleValue === filterValue;
                } else {
                    return titleValue.includes(filterValue);
                }
            }

            return true;
        });
    });

    const handleAppointmentClick = (appointment: Appointment) => {
        const returnUrl = `${location.pathname}${location.search}`;
        sessionStorage.setItem('appointmentsReturnUrl', returnUrl);
        push(`/appointments/details/${appointment.id}`);
    };

    // Sync view mode with URL changes
    useEffect(() => {
        const pathSegments = location.pathname.split('/');
        const viewParam = pathSegments[2]; // /appointments/:view
        const newViewMode = viewParam === 'list' ? 'list' : 'calendar';
        setViewMode(newViewMode);
    }, [location.pathname]);

    // Handle view mode change
    const handleViewModeChange = (mode: 'calendar' | 'list') => {
        setViewMode(mode);
        push(`/appointments/${mode}`);
    };
    // Update URL when filters, page, or calendar month changes
    useEffect(() => {
        const params = new URLSearchParams();

        if (filterValues.length > 0) {
            params.set('filters', JSON.stringify(filterValues));
        }

        if (currentPage > 1) {
            params.set('page', currentPage.toString());
        }

        if (viewMode === 'calendar') {
            const currentMonth = new Date();
            // Only add month param if it's not the current month
            if (
                selectedCalendarDate.getMonth() !== currentMonth.getMonth() ||
                selectedCalendarDate.getFullYear() !== currentMonth.getFullYear()
            ) {
                const monthParam = selectedCalendarDate.toISOString().split('T')[0];
                if (monthParam) {
                    params.set('month', monthParam);
                }
            }
        }

        const newSearch = params.toString();
        const currentSearch = searchParams.toString();

        // Only update if changed to avoid unnecessary navigation
        if (newSearch !== currentSearch) {
            setSearchParams(params, { replace: true });
        }
    }, [filterValues, currentPage, selectedCalendarDate, viewMode, setSearchParams, searchParams]);

    const handleFilterChange = (filters: StanFilterValue[]) => {
        setFilterValues(filters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilterValues([]);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleCalendarNavigate = (date: Date) => {
        setSelectedCalendarDate(date);
    };

    const handleBlockTime = async (blockTimeParams: {
        id?: string;
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
    }) => {
        await blockTime(blockTimeParams);
        await refetchCalendar();
    };

    const handleDeleteBlockedTime = async (blockedTimeId: string) => {
        await deleteBlockedTime({ blockedTimeId });
        await refetchCalendar();
    };

    const handleGetAppointmentsForDay = (date: Date) => {
        return getAppointmentsForDay(calendarAppointments, date);
    };

    return {
        // State
        viewMode,
        filterValues,
        currentPage,
        selectedCalendarDate,

        // Calendar data
        calendarAppointments,
        calendarEvents,
        calendarLoading,
        calendarIsError,
        calendarError,

        // List data
        listAppointments: listAppointmentsFiltered,
        listLoading,
        listIsError,
        listError,
        pagination,

        // Block time
        isBlocking,
        isDeleting,

        // Handlers
        handleAppointmentClick,
        handleViewModeChange,
        handleFilterChange,
        handleResetFilters,
        handlePageChange,
        handleCalendarNavigate,
        handleBlockTime,
        handleDeleteBlockedTime,
        handleGetAppointmentsForDay,

        // Refetch functions
        refetchCalendar,
        refetchList,
    };
}
